import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../lib/i18n.js';
import { useSession } from '../stores/session.js';
import { getTenantSettings, setAllowedIpRanges } from '../lib/assistant-settings-api.js';
import { errorMessage } from '../lib/properties.js';
import { IP_OR_CIDR_PATTERN, isIpAllowed } from '../lib/ip-match.js';

const SECURITY_QUERY_KEY = ['nastaveni', 'security-ip'] as const;

function parseRanges(text: string): readonly string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/**
 * Krok UI8 bod 2 — Nastavení → Zabezpečení: IP rozsahy pro přihlášení
 * (API hotovo v SR8b). Ochrana „nezamkni se": uložení rozsahu, který
 * nepokrývá aktuální IP, vyžaduje potvrzení (viz `ip-security-card.spec.tsx`).
 */
export function IpSecurityCard(): React.JSX.Element {
  const t = useT();
  const user = useSession((s) => s.user);
  const queryClient = useQueryClient();
  const isOwnerOrAdmin = (user?.roles ?? []).some((r) => r === 'owner' || r === 'admin');

  const settings = useQuery({ queryKey: SECURITY_QUERY_KEY, queryFn: getTenantSettings });

  // Render-time derive misto useEffect (reos-web-admin-mount-effect-race):
  // efekt po pripojeni by mohl prepsat rozepsany text mezi commitem a behem efektu.
  const [seenData, setSeenData] = React.useState(settings.data);
  const [text, setText] = React.useState('');
  if (seenData !== settings.data) {
    setSeenData(settings.data);
    if (settings.data !== undefined) setText(settings.data.allowedIpRanges.join('\n'));
  }

  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [invalidLine, setInvalidLine] = React.useState<string | null>(null);

  async function save(ranges: readonly string[]): Promise<void> {
    setBusy(true);
    setNote(null);
    try {
      await setAllowedIpRanges(ranges);
      await queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEY });
      setNote({ ok: true, text: t('webAdmin.settings.securitySaved') });
    } catch (e) {
      setNote({ ok: false, text: t('webAdmin.settings.securityError', { error: errorMessage(e) }) });
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setInvalidLine(null);
    const ranges = parseRanges(text);
    const bad = ranges.find((r) => !IP_OR_CIDR_PATTERN.test(r));
    if (bad !== undefined) {
      setInvalidLine(t('webAdmin.settings.securityInvalidRange', { value: bad }));
      return;
    }
    const currentIp = settings.data?.currentIp;
    if (currentIp !== undefined && !isIpAllowed(currentIp, ranges)) {
      const confirmed = window.confirm(t('webAdmin.settings.securityLockoutWarning', { ip: currentIp }));
      if (!confirmed) return;
    }
    await save(ranges);
  }

  return (
    <div className="rh-card" style={{ maxWidth: 640, padding: 16, marginBottom: 16 }} data-testid="nastaveni-security-card">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.securityTitle')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.settings.securityPerex')}</p>

      {settings.data !== undefined ? (
        <p className="mb-2 text-xs text-gray-600" data-testid="security-current-ip">
          {t('webAdmin.settings.securityCurrentIpLabel', { ip: settings.data.currentIp })}
        </p>
      ) : null}

      <form onSubmit={(e) => void onSubmit(e)} data-testid="security-ip-form">
        <label className="mb-1 block text-sm font-medium" htmlFor="security-ip-ranges">
          {t('webAdmin.settings.securityIpRangesLabel')}
        </label>
        <textarea
          id="security-ip-ranges"
          data-testid="security-ip-ranges"
          className="mb-1 block w-full rounded border px-2 py-1 text-sm"
          rows={4}
          disabled={!isOwnerOrAdmin || settings.isLoading}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <p className="mb-2 text-xs text-gray-600">{t('webAdmin.settings.securityIpRangesHint')}</p>
        {invalidLine !== null ? (
          <p className="mb-2 text-sm text-red-700" role="alert" data-testid="security-invalid-range">
            {invalidLine}
          </p>
        ) : null}
        <button
          type="submit"
          className="rh-a2-btn rh-a2-btn--primary"
          disabled={!isOwnerOrAdmin || busy}
          data-testid="security-ip-save"
        >
          {t('webAdmin.settings.securitySaveButton')}
        </button>
      </form>
      {note !== null ? (
        <p className={`mt-2 text-sm ${note.ok ? 'text-green-700' : 'text-red-700'}`} role={note.ok ? 'status' : 'alert'} data-testid="security-note">
          {note.text}
        </p>
      ) : null}
    </div>
  );
}
